import { useQuery } from '@tanstack/react-query';
import { useCurrentCompany } from '../auth/AuthProvider';
import { get } from '../../lib/apiClient';
import type { AddonsState } from './addonOffers';
import type { WidgetState } from './WidgetUsage';

/**
 * The current company's plan and ticket usage, cached once.
 *
 * This lived inside `TopBar`, which was fine while the free-tier chip was the
 * only thing that needed it. The ticket page needs the plan too, to say
 * whether an expired walkthrough clip is a fact or an upgrade (TOADO-367),
 * and a second copy of the fetch would have had to keep the same query key by
 * agreement rather than by construction. One key, one fetcher, one place to
 * change the endpoint.
 *
 * Null means "no billing data", not "free". Callers must not read an absent
 * answer as a tier: a failed request would otherwise tell a paying workspace
 * it is on the free plan.
 */
export interface PlanPrice {
  /** Cents for one month, per seat where `perSeat`. */
  monthlyCents: number;
  /** Cents for one year, per seat where `perSeat`. */
  annualCents: number;
  perSeat: boolean;
  minSeats: number;
  maxSeats: number | null;
  /**
   * What this plan's year saves: "4 months free" on Personal, "2 months free"
   * on Team and Business (TOADO-865). Absent from an API older than the page,
   * during a deploy, and then the price is shown without it.
   */
  annualSaving?: string;
}

export interface BillingCatalog {
  /** Business only where it is on sale (TOADO-662). */
  plans: { personal: PlanPrice; team: PlanPrice; business?: PlanPrice };
  contactUrl: string;
}

/**
 * The fewest seats a plan is sold with: the server's catalogue says, and
 * Business is three where it is not on sale (a workspace already on it).
 */
export function minSeatsFor(plan: string, catalog: BillingCatalog | undefined): number {
  const price = (catalog?.plans as Record<string, PlanPrice | undefined> | undefined)?.[plan];
  if (price) return price.minSeats;
  return plan === 'business' ? 3 : 1;
}

/** What the plan subscription bills per unit, as Stripe has it (an older price stays). */
export interface HeldPlanPrice {
  unitAmountCents: number;
  currency: string;
  interval: 'monthly' | 'annual';
}

export interface SubscriptionState {
  plan: 'free' | 'personal' | 'team' | 'business' | 'enterprise';
  planInterval: 'monthly' | 'annual' | null;
  subscriptionStatus: 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'paused';
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  seatsPurchased: number;
  /** What the plan bills as Stripe has it; null where it could not be read (no price is then shown). */
  currentPrice?: HeldPlanPrice | null;
  ticketsUsedThisMonth: number | null;
  ticketsCap?: number;
  ticketsResetsAt?: string;
  /**
   * False on dev/staging environments where Stripe env vars aren't set.
   * The full billing UI still renders (current plan + plan cards + tickets
   * usage), but checkout / portal buttons surface a dev-mode toast instead
   * of redirecting. Defaults to true when the field is absent so older
   * API responses keep working.
   */
  billingConfigured?: boolean;
  /**
   * What Toado charges, from the server's one copy (TOADO-658). Optional only
   * so a page that loads ahead of its API during a deploy still renders.
   */
  catalog?: BillingCatalog;
  /** Add-ons on offer, renewing and held (TOADO-660). Absent from an older API. */
  addons?: AddonsState;
  /** The widget's reports, visitor minutes and tier (TOADO-661). On paid plans only. */
  widget?: WidgetState;
  /** Present when the viewer can record in this workspace (TOADO-497). Null limits are unlimited. */
  recording?: {
    recordedMinutes: number;
    recordingMinutes: number | null;
    transcribedMinutes: number;
    transcriptionMinutes: number | null;
    storageBytes: number;
    storageLimitBytes: number | null;
    maxRecordingMinutes: number;
    videoRetentionDays: number;
    resetsAt: string;
    /** What the plan includes, before add-ons (TOADO-660). */
    included?: { recordingMinutes: number | null; transcriptionMinutes: number | null; storageBytes: number | null };
    boosts?: { recordingMinutes: number; transcriptionMinutes: number };
    packs?: { recordingMinutes: number; transcriptionMinutes: number };
    videoHistory?: { active: boolean; through: string | null };
  };
}

/** What the top bar and a ticket read of it. */
export type BillingSummary = SubscriptionState;

/** The cache key every reader of a workspace's billing shares. */
export const billingQueryKey = (companyId: string | undefined) => ['billing', companyId] as const;

/**
 * The workspace's subscription, as the billing tab and the top bar both read
 * it. It throws on a failed request, so a failure is an error for every
 * reader rather than an answer of nothing that one reader caches for the
 * others (review of 0.2.0, 3 Oct: the tab threw and the hook cached null, on
 * one key).
 */
export function fetchSubscription(companyId: string): Promise<SubscriptionState> {
  return get<SubscriptionState>(`/v1/billing/subscription?companyId=${encodeURIComponent(companyId)}`);
}

/**
 * The workspace's billing, or null while it is loading, when it could not be
 * read, and when there is none: an absent answer, never a tier. A reader that
 * must tell a failure apart reads the query itself (`billingQueryKey`).
 *
 * @param companyId Whose billing to read. Defaults to the selected workspace,
 *   which is right for chrome like the top bar. Anything showing a plan for a
 *   specific RECORD has to pass that record's workspace: a ticket opened by
 *   link can belong to a workspace other than the selected one, and reading
 *   the wrong plan there offers an upgrade for a workspace that already pays.
 */
export function useBillingSummary(companyId?: string): BillingSummary | null {
  const currentCompany = useCurrentCompany();
  const id = companyId ?? currentCompany?.id;
  const { data } = useQuery({
    queryKey: billingQueryKey(id),
    queryFn: () => fetchSubscription(id!),
    enabled: !!id,
    // Long enough that page navigation does not re-ask, short enough that an
    // upgrade shows up without a reload.
    staleTime: 60 * 1000,
  });
  return data ?? null;
}
